import {ThemeProvider} from '@emotion/react';
import {useLocation, useNavigate} from 'react-router-dom';
import theme from '../../theme';
import GenericFinancialRecordComponent from './component';
import {usePaymentList} from '../PaymentList/hooks';
import {useInsuranceClaims} from '../InsuranceClaims/hooks';
import {getModifiedColumns} from './utils';
import {FinanceRecordType, FinancialRecordData, PatientData} from './types';
import {Payment} from '~/api/finance/paymentList';
import {InsuranceClaim} from '~/api/finance/insuranceClaims/insuranceList';
import {useState} from 'react';
import SearchPatientContainer from '../../common/components/SearchPatientModel';
import {ICustomTableColumn} from '~/components/CustomTable';
import FinancialRecordActionHandler from './FinancialRecordActionHandler';
import {getInsuranceClaimStatusDisplay} from '../../utils';
import {RouteSection, getRoute} from '~/utils/route';
import {RoutePath} from '~/constants/routes';

const FinancialRecordContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const patientId = location.state?.patient_id;
  const recordType: FinanceRecordType = location.state?.type || 'payment';

  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  const {
    isLoading: paymentsLoading,
    isError: paymentsError,
    message: paymentsMessage,
    payments,
    patient: paymentPatient,
    totalRecords: paymentsTotalRecords,
  } = usePaymentList({
    patientId: recordType === 'payment' ? patientId : undefined,
    pageNumber: currentPage + 1,
    perPage: pageSize,
    orderDirection: 'desc',
  });

  const {
    data: claimsData,
    isLoading: claimsLoading,
    isError: claimsError,
    error: claimsErrorObj,
  } = useInsuranceClaims(
    recordType === 'insurance-claim' && patientId
      ? {
          patientId,
          pageNumber: currentPage + 1,
          perPage: pageSize,
          sortDirection: 'desc',
        }
      : {}
  );

  const claims = claimsData?.insuranceClaims || [];
  const claimsPatient: PatientData =
    claimsData?.patient ||
    (claims.length > 0
      ? {
          id: claims[0].patient_id || patientId,
          full_name: claims[0].patientName,
        }
      : null) ||
    null;
  const claimsMessage =
    claimsErrorObj?.message || 'Could not get insurance claims list!';

  if (!patientId) {
    return <div>No patient ID provided</div>;
  }

  const handleRowClick = (row: FinancialRecordData) => {
    if (recordType === 'payment') {
      const payment = row as Payment;
      navigate(
        getRoute(
          RouteSection.Finance,
          `${RoutePath.staff.finance.payments}/${payment.id}`
        )
      );
    } else {
      const claim = row as InsuranceClaim;
      navigate(
        getRoute(
          RouteSection.Finance,
          RoutePath.staff.finance.insuranceClaimDetails.replace(
            ':id',
            claim.id.toString()
          )
        )
      );
    }
  };

  const transformPaymentsData = (
    payments: Payment[]
  ): FinancialRecordData[] => {
    if (!Array.isArray(payments)) {
      return [];
    }
    return payments.map(payment => ({
      ...payment,
      paymentMethodType: payment.paymentMethodType ?? '-',
      amount: payment.amount ?? '-',
      billingDate: payment.billingDate ?? '-',
      description: payment.description ?? '-',
      invoiceNumber: payment.invoiceNumber ?? '-',
      patientName: payment.patientName ?? '-',
      status: payment.status ?? '-',
    }));
  };

  const transformClaimsData = (
    claims: InsuranceClaim[]
  ): FinancialRecordData[] => {
    if (!Array.isArray(claims)) {
      return [];
    }
    return claims.map(claim => ({
      ...claim,
      membershipNo: claim.membershipNo ?? '-',
      insurerName: claim.insurerName ?? '-',
      status: claim.status ?? '-',
      outstandingAmount: claim.outstandingAmount ?? 0,
      patientName: claim.patientName ?? '-',
    }));
  };

  const getInsuranceClaimColumns =
    (): ICustomTableColumn<FinancialRecordData>[] => {
      const columns: ICustomTableColumn<FinancialRecordData>[] = [
        {
          key: 'membershipNo',
          label: 'Membership No',
          isBold: true,
          align: 'center',
        },
        {
          key: 'insurerName',
          label: 'Insurer',
          align: 'center',
        },

        {
          key: 'outstandingAmount',
          label: 'Outstanding (£)',
          align: 'center',
          render: (_: unknown, row: FinancialRecordData) => {
            const amount = (row as InsuranceClaim).outstandingAmount;
            if (
              amount === null ||
              amount === undefined ||
              isNaN(amount) ||
              amount === 0
            ) {
              return <span>-</span>;
            }
            return <span>£{amount.toFixed(2)}</span>;
          },
        },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: (_: unknown, row: FinancialRecordData) => {
            const getStatusClass = (status: string) => {
              const normalizedStatus = status.toLowerCase().replace(' ', '_');
              return `status-badge status-${normalizedStatus}`;
            };

            const displayStatus = getInsuranceClaimStatusDisplay(row.status);

            return (
              <span className={getStatusClass(row.status)}>
                {displayStatus}
              </span>
            );
          },
        },
        {
          key: 'actions',
          label: 'Actions',
          align: 'center',
          render: (_: unknown, row: FinancialRecordData) => (
            <FinancialRecordActionHandler
              rowData={row}
              recordType={recordType}
            />
          ),
        },
      ];

      return columns;
    };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const isLoading = recordType === 'payment' ? paymentsLoading : claimsLoading;
  const isError = recordType === 'payment' ? paymentsError : claimsError;
  const message = recordType === 'payment' ? paymentsMessage : claimsMessage;
  const data: FinancialRecordData[] =
    recordType === 'payment' ? payments : claims;
  const patient: PatientData =
    recordType === 'payment' ? paymentPatient : claimsPatient;
  const totalCount =
    recordType === 'payment'
      ? paymentsTotalRecords
      : claimsData?.totalRecords || 0;
  const tableColumns =
    recordType === 'payment'
      ? getModifiedColumns(recordType)
      : getInsuranceClaimColumns();
  const transformData = (
    data: FinancialRecordData[]
  ): FinancialRecordData[] => {
    if (recordType === 'payment') {
      return transformPaymentsData(data as Payment[]);
    } else {
      return transformClaimsData(data as InsuranceClaim[]);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <GenericFinancialRecordComponent
        data={data}
        patient={patient}
        isLoading={isLoading}
        isError={isError}
        message={message}
        recordType={recordType}
        tableColumns={tableColumns}
        transformData={transformData}
        onActionButtonClick={() => setModalOpen(true)}
        onRowClick={handleRowClick}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
      />
      {modalOpen && (
        <SearchPatientContainer
          newAppointmentModalOpen={modalOpen}
          handleNewAppointmentModalOpen={() => setModalOpen(false)}
          type={
            recordType === 'insurance-claim' ? 'insurance_claim' : 'payment'
          }
        />
      )}
    </ThemeProvider>
  );
};

export default FinancialRecordContainer;
